import { useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9quirvnahb7382ovgb0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
        setFormStatus('error');
        setFormMessage(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查您的连接后重试');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于黄油游戏大全',
            description: '黄油游戏大全是一个专注于黄油游戏资源收录与推荐的中文导航平台，致力于为玩家提供最全的游戏分类、详细的评测攻略以及可靠的下载指引。',
            url: 'https://hdyxdaquan.com/about',
          }),
        }}
      />

      <div className="pt-20 pb-10 bg-background-50">
        <div className="w-full px-4 md:px-6">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          <section className="max-w-4xl mx-auto mt-6">
            <header className="text-center mb-12">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-primary-500 text-background-50">
                <i className="ri-gamepad-line text-2xl"></i>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading text-foreground-900 mb-4">关于黄油游戏大全</h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                黄油游戏大全成立于2024年，是一个专注于黄油游戏资源收录与推荐的中文导航平台。我们致力于为玩家提供最全面的游戏分类、最详细的评测攻略以及最可靠的下载指引。
              </p>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
              <div className="bg-background-100 rounded-xl p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500">
                  <i className="ri-focus-3-line text-xl"></i>
                </div>
                <h2 className="text-base font-heading text-foreground-900 mb-2">精准收录</h2>
                <p className="text-sm text-foreground-500">严格筛选每一款收录游戏，确保信息准确、评测客观，为玩家提供最可靠的游戏推荐。</p>
              </div>
              <div className="bg-background-100 rounded-xl p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-accent-100 text-accent-500">
                  <i className="ri-guide-line text-xl"></i>
                </div>
                <h2 className="text-base font-heading text-foreground-900 mb-2">专业攻略</h2>
                <p className="text-sm text-foreground-500">资深玩家团队撰写深度攻略，从入门到进阶，帮助每一位玩家快速上手、畅享游戏乐趣。</p>
              </div>
              <div className="bg-background-100 rounded-xl p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-500">
                  <i className="ri-heart-line text-xl"></i>
                </div>
                <h2 className="text-base font-heading text-foreground-900 mb-2">玩家至上</h2>
                <p className="text-sm text-foreground-500">以玩家需求为核心，持续优化平台体验，打造最贴心的黄油游戏一站式导航服务。</p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl p-10 text-center mb-14">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                <div>
                  <div className="text-3xl md:text-4xl font-heading text-background-50">800+</div>
                  <div className="text-sm text-background-50/70 mt-1">收录游戏</div>
                </div>
                <div>
                  <div className="text-3xl md:text-4xl font-heading text-background-50">6</div>
                  <div className="text-sm text-background-50/70 mt-1">游戏分类</div>
                </div>
                <div>
                  <div className="text-3xl md:text-4xl font-heading text-background-50">300+</div>
                  <div className="text-sm text-background-50/70 mt-1">精品攻略</div>
                </div>
                <div>
                  <div className="text-3xl md:text-4xl font-heading text-background-50">50万+</div>
                  <div className="text-sm text-background-50/70 mt-1">月活玩家</div>
                </div>
              </div>
            </div>

            <section className="mb-14">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-8 text-center">玩家口碑</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    name: '游戏猎人',
                    role: '资深黄油玩家',
                    text: '黄油游戏大全是我每天必逛的网站，分类清晰、评测专业，帮我发现了很多之前不知道的好游戏。强烈推荐给每一位黄油游戏爱好者！',
                  },
                  {
                    name: '小樱',
                    role: 'AVG游戏爱好者',
                    text: '作为AVG游戏的忠实粉丝，我在这里找到了很多优质的视觉小说推荐。攻略文章写得很用心，对新手非常友好。',
                  },
                  {
                    name: '策略大师K',
                    role: 'SLG游戏达人',
                    text: '网站的SLG游戏攻略非常专业，从资源管理到战斗策略都讲得很透彻。通过这里的推荐入坑了好几款佳作，每一款都玩得很开心。',
                  },
                ].map((t, i) => (
                  <div key={i} className="bg-background-100 rounded-xl p-6">
                    <div className="flex items-center gap-1 mb-3">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <i key={s} className="ri-star-fill text-amber-400 text-xs"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">{t.text}</p>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                        <i className="ri-user-line text-primary-500 text-xs"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-800">{t.name}</div>
                        <div className="text-xs text-foreground-400">{t.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="max-w-2xl mx-auto mb-14">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-8 text-center">联系我们</h2>
              <div className="bg-background-100 rounded-xl p-8">
                <form onSubmit={handleSubmit} data-readdy-form="">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="about-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100/50 transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        id="about-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100/50 transition-all"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="about-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      type="text"
                      id="about-subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100/50 transition-all"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100/50 transition-all resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500字</p>
                  </div>
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  <button
                    type="submit"
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    发送留言
                  </button>
                  {formStatus !== 'idle' && (
                    <div className={`mt-4 p-3 rounded-lg text-sm ${formStatus === 'success' ? 'bg-accent-100 text-accent-800' : 'bg-primary-100 text-primary-800'}`}>
                      {formMessage}
                    </div>
                  )}
                </form>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
                <div className="text-center">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-800 mb-1">电子邮箱</h3>
                  <p className="text-sm text-foreground-500">contact@hdyxdaquan.com</p>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-xl bg-accent-100 text-accent-500">
                    <i className="ri-chat-3-line text-lg"></i>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-800 mb-1">在线客服</h3>
                  <p className="text-sm text-foreground-500">工作日 9:00 - 18:00</p>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-500">
                    <i className="ri-share-line text-lg"></i>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-800 mb-1">社交媒体</h3>
                  <p className="text-sm text-foreground-500">关注我们的微博和B站</p>
                </div>
              </div>
            </section>
          </section>
        </div>
      </div>
    </>
  );
}